import React, { useEffect, useState } from "react";
import "../Css/AdminDashboard.css";
import "../Css/DispatcherDashboard.css";
import { FiShoppingCart, FiPackage, FiTruck, FiCheckCircle, FiXCircle, FiUsers, FiClock, FiAlertTriangle, FiUserX } from "react-icons/fi";
import "leaflet/dist/leaflet.css";
import Navbar from './Navbar';
import "../Css/leaflet-fix.css";
import { useNavigate } from "react-router-dom";
import AssignModal from './AssignModal';
import Logger from "./Logger";
import { getLoggerBaseURL } from "../utils/getLoggerBaseURL";

// Initialize Logger
const logger = new Logger(getLoggerBaseURL());

// ─── Tiny reusable primitives ────────────────────────────────────────────────

/** Animated shimmer skeleton block */
const Skeleton = ({ width = "100%", height = 20, radius = 6, style = {} }) => (
    <div style={{
        width, height,
        borderRadius: radius,
        background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
        backgroundSize: "200% 100%",
        animation: "skeletonShimmer 1.4s infinite",
        ...style
    }} />
);

/** Thin top progress bar (NProgress-style) */
const TopProgressBar = ({ visible }) => (
    <div style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 9999,
        height: 3,
        background: "transparent",
        pointerEvents: "none",
    }}>
        <div style={{
            height: "100%",
            width: visible ? "85%" : "100%",
            background: "linear-gradient(90deg, #3b82f6, #6366f1)",
            transition: visible
                ? "width 0.4s ease"
                : "width 0.3s ease, opacity 0.4s ease 0.1s",
            opacity: visible ? 1 : 0,
            borderRadius: "0 2px 2px 0",
            boxShadow: "0 0 8px #3b82f680",
        }} />
    </div>
);

/** Inline spinner */
const Spinner = ({ size = 20, color = "#3b82f6" }) => (
    <div style={{
        width: size, height: size,
        border: `2px solid ${color}22`,
        borderTop: `2px solid ${color}`,
        borderRadius: "50%",
        animation: "spin 0.7s linear infinite",
        flexShrink: 0,
    }} />
);

/** Section-level loading placeholder */
const SectionLoader = ({ height = 120, label = "Loading…" }) => (
    <div style={{
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        height, gap: 10, color: "#94a3b8", fontSize: 13,
    }}>
        <Spinner />
        <span>{label}</span>
    </div>
);

// ─── Inject keyframes once ────────────────────────────────────────────────────
if (typeof document !== "undefined" && !document.getElementById("dd-keyframes")) {
    const s = document.createElement("style");
    s.id = "dd-keyframes";
    s.textContent = `
        @keyframes skeletonShimmer {
            0%   { background-position: 200% 0; }
            100% { background-position: -200% 0; }
        }
        @keyframes spin {
            to { transform: rotate(360deg); }
        }
    `;
    document.head.appendChild(s);
}

// ─── KPI skeleton card ────────────────────────────────────────────────────────
const KPISkeletonTile = () => (
    <div className="kpi-tile kpi-blue" style={{ gap: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Skeleton width={90} height={14} />
            <Skeleton width={28} height={28} radius={8} />
        </div>
        <Skeleton width={60} height={32} radius={6} />
        <Skeleton width={120} height={12} />
    </div>
);

// ─── Component ────────────────────────────────────────────────────────────────
const DispatcherDashboard = () => {
    const [activeDriversList, setActiveDriversList] = useState([]);
    const [tableDriver, setTableDriver] = useState([]);
    const [dateRange] = useState("today");
    const userEmail = sessionStorage.getItem("userEmail");
    const [liveStats, setLiveStats] = useState({ total: 0, unassigned: 0, inProgress: 0, completed: 0 });
    const [showAllDrivers, setShowAllDrivers] = useState(false);
    const [orders, setOrders] = useState([]);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [assignOrderId, setAssignOrderId] = useState(null);
    const [assignActionType, setAssignActionType] = useState("assign");
    const [assignReason, setAssignReason] = useState("");
    const [selectedOrders, setSelectedOrders] = useState([]);
    const [showDriverOrdersModal, setShowDriverOrdersModal] = useState(false);
    const [selectedDriverForModal, setSelectedDriverForModal] = useState(null);
    const [driverModalOrders, setDriverModalOrders] = useState([]);
    const [driverModalLoading, setDriverModalLoading] = useState(false);
    const [driverActiveOrdersMap, setDriverActiveOrdersMap] = useState(new Map());
    const [showTodayOnly, setShowTodayOnly] = useState(false);

    // ── Granular loading flags ────────────────────────────────────────────────
    const [kpiLoading, setKpiLoading] = useState(true);
    const [ordersLoading, setOrdersLoading] = useState(true);
    const [driversLoading, setDriversLoading] = useState(true);
    const [activeOrdersLoading, setActiveOrdersLoading] = useState(false);
    /** true while the very first full load is in flight → shows top progress bar */
    const [initialLoading, setInitialLoading] = useState(true);

    const navigate = useNavigate();

    // ── Fetch helpers ─────────────────────────────────────────────────────────
    const fetchData = async () => {
        setDriversLoading(true);
        try {
            const res = await fetch(`/api/dispatches/${userEmail}`);
            const driverData = await res.json();
            const list = Array.isArray(driverData) ? driverData : [];
            setActiveDriversList(list);
            setTableDriver(list);
            return list;
        } catch (err) {
            logger.log(`Fetch error: ${err}`, "ERROR");
        } finally {
            setDriversLoading(false);
        }
    };

    const fetchKPIStats = async () => {
        setKpiLoading(true);
        try {
            const res = await fetch(`/api/dashboard/kpi/${userEmail}`);
            if (!res.ok) throw new Error(`API error: ${res.status}`);
            const data = await res.json();
            setLiveStats({
                total: data.total || 0,
                unassigned: data.unassigned || 0,
                inProgress: data.inProgress || 0,
                completed: data.completed || 0,
                failed: data.failed || 0,
                billsForCollection: data.billsForCollection || 0,
                totalDueAmountForCollection: data.totalDueAmountForCollection || 0,
                activeDrivers: data.activeDrivers || 0,
                totalDrivers: data.totalDrivers || 0,
                today: data.today || {},
                yesterday: data.yesterday || {}
            });
        } catch (err) {
            logger.log(`KPI fetch error: ${err}`, "ERROR");
        } finally {
            setKpiLoading(false);
        }
    };

    const fetchOrders = async () => {
        setOrdersLoading(true);
        try {
            const res = await fetch(`/api/ordera/ordersAllData/${userEmail}`);
            if (!res.ok) throw new Error(`API error: ${res.status}`);
            const data = await res.json();
            setOrders(Array.isArray(data) ? data : (data.data || []));
        } catch (err) {
            logger.log(`Orders fetch error: ${err}`, "ERROR");
        } finally {
            setOrdersLoading(false);
        }
    };

    const fetchAllDriversActiveOrders = async (driversList) => {
        if (!driversList?.length) return;
        setActiveOrdersLoading(true);
        try {
            const results = await Promise.all(
                driversList.map(async (driver) => {
                    try {
                        const res = await fetch(`/api/DrOrder/order/${driver.email}`);
                        if (!res.ok) throw new Error();
                        const data = await res.json();
                        const count = Array.isArray(data)
                            ? data.filter(o => o.o_status !== "2" && o.o_status !== "3").length
                            : 0;
                        return [driver.email, count];
                    } catch {
                        return [driver.email, 0];
                    }
                })
            );
            setDriverActiveOrdersMap(new Map(results));
        } catch (err) {
            logger.log(`Error fetching active order counts: ${err}`, "ERROR");
        } finally {
            setActiveOrdersLoading(false);
        }
    };

    const fetchDriverOrders = async (driver) => {
        setDriverModalLoading(true);
        try {
            const res = await fetch(`/api/DrOrder/order/${driver.email}`);
            if (!res.ok) throw new Error();
            const data = await res.json();
            const active = Array.isArray(data)
                ? data.filter(o => o.o_status !== "2" && o.o_status !== "3")
                : [];
            setDriverModalOrders(active);
        } catch (err) {
            logger.log(`Error fetching driver orders: ${err}`, "ERROR");
            setDriverModalOrders([]);
        } finally {
            setDriverModalLoading(false);
        }
    };

    // ── Initial load ──────────────────────────────────────────────────────────
    useEffect(() => {
        const loadDashboard = async () => {
            setInitialLoading(true);
            const [, driverList] = await Promise.all([
                fetchKPIStats(),
                fetchData(),
                fetchOrders(),
            ]);
            // Fire active orders fetch non-blocking — don't await it
            if (driverList?.length) fetchAllDriversActiveOrders(driverList);
            setInitialLoading(false);
        };
        loadDashboard();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userEmail, dateRange]);

    // ── Delay helpers ─────────────────────────────────────────────────────────
    const getDelayHours = (delTime, delDate) => {
        if (!delTime || !delDate) return 0;
        const deliveryDateTime = new Date(`${delDate}T${delTime}`);
        return (new Date() - deliveryDateTime) / (1000 * 60 * 60);
    };
    const isDelayed6h = o => { const h = getDelayHours(o.Del_Time, o.Del_Date); return h > 6 && h <= 12; };
    const isDelayed12h = o => { const h = getDelayHours(o.Del_Time, o.Del_Date); return h > 12 && h <= 24; };
    const isDelayed24h = o => { const h = getDelayHours(o.Del_Time, o.Del_Date); return h > 24; };

    const inactiveDriversCount = Array.isArray(tableDriver) ? tableDriver.filter(d => d.status !== true).length : 0;
    const delayed6hOrders = Array.isArray(orders) ? orders.filter(isDelayed6h) : [];
    const delayed12hOrders = Array.isArray(orders) ? orders.filter(isDelayed12h) : [];
    const delayed24hOrders = Array.isArray(orders) ? orders.filter(isDelayed24h) : [];
    const totalDelayed = delayed6hOrders.length + delayed12hOrders.length + delayed24hOrders.length;

    const alertsData = [
        { label: "Orders delayed > 24 hrs", count: delayed24hOrders.length, icon: <FiAlertTriangle />, color: "red" },
        { label: "Orders delayed > 12 hrs", count: delayed12hOrders.length, icon: <FiClock />, color: "orange" },
        { label: "Orders delayed > 6 hrs", count: delayed6hOrders.length, icon: <FiClock />, color: "yellow" },
        { label: "Inactive drivers", count: inactiveDriversCount, icon: <FiUserX />, color: "gray" },
    ];

    const getChange = (today = 0, yesterday = 0) => {
        if (!yesterday && !today) return "No data";
        if (!yesterday) return "No data";
        const diff = today - yesterday;
        const percent = ((diff / yesterday) * 100).toFixed(1);
        return `${diff >= 0 ? "+" : ""}${percent}% vs yesterday`;
    };

    const displayStats = showTodayOnly
        ? { total: liveStats.today?.total || 0, unassigned: liveStats.today?.unassigned || 0, inProgress: liveStats.today?.inProgress || 0, completed: liveStats.today?.completed || 0, failed: liveStats.today?.failed || 0, activeDrivers: liveStats.activeDrivers || 0 }
        : { total: liveStats.total || 0, unassigned: liveStats.unassigned || 0, inProgress: liveStats.inProgress || 0, completed: liveStats.completed || 0, failed: liveStats.failed || 0, activeDrivers: liveStats.activeDrivers || 0 };

    const KPITile = ({ title, value, change, color, icon }) => (
        <div className={`kpi-tile kpi-${color}`}>
            <div className="kpi-header">
                <div className="kpi-title">{title}</div>
                <div className={`kpi-icon icon-${color}`}>{icon}</div>
            </div>
            <div className="kpi-value">{value}</div>
            <div className={`kpi-change ${color}`}>{change}</div>
        </div>
    );

    const sortedDrivers = [...activeDriversList].sort((a, b) => {
        if (a.status !== b.status) return b.status ? 1 : -1;
        return (Number(driverActiveOrdersMap.get(b.email)) || 0) - (Number(driverActiveOrdersMap.get(a.email)) || 0);
    });

    const handleAssignClick = (orderId) => {
        setAssignOrderId(orderId);
        setAssignActionType("assign");
        setAssignReason("");
        setSelectedOrders([]);
        setShowAssignModal(true);
    };
    const handleCloseAssignModal = () => { setShowAssignModal(false); setAssignOrderId(null); };
    const handleAssignmentStatusUpdated = async () => { await Promise.all([fetchKPIStats(), fetchOrders()]); };
    const handleAssignmentComplete = () => setSelectedOrders([]);
    const handleDriverClick = (driver) => { setSelectedDriverForModal(driver); setShowDriverOrdersModal(true); fetchDriverOrders(driver); };

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <>
            <Navbar />

            {/* Top progress bar — visible during initial load */}
            <TopProgressBar visible={initialLoading} />

            <div className="dashboard-container">
                <div className="dashboard-header">
                    <h2 className="dashboard-heading">
                        Dashboard
                        {initialLoading && (
                            <span style={{ marginLeft: 10, display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, color: "#94a3b8", fontWeight: 400 }}>
                                <Spinner size={14} />
                                Loading…
                            </span>
                        )}
                    </h2>
                    <button className="dashboard-toggle-button" onClick={() => setShowTodayOnly(p => !p)}>
                        {showTodayOnly ? "Show Total" : "Show Today"}
                    </button>
                </div>

                {/* ── KPI Grid ── */}
                <div className="kpi-grid">
                    {kpiLoading ? (
                        // 6 skeleton tiles matching the real grid
                        Array.from({ length: 6 }).map((_, i) => <KPISkeletonTile key={i} />)
                    ) : (
                        <>
                            <KPITile title="Total Orders" value={displayStats.total} change={getChange(displayStats.total, liveStats.yesterday?.total)} color="blue" icon={<FiShoppingCart />} />
                            <KPITile title="Unassigned" value={displayStats.unassigned} change={getChange(displayStats.unassigned, liveStats.yesterday?.unassigned)} color="orange" icon={<FiPackage />} />
                            <KPITile title="In Progress" value={displayStats.inProgress} change={getChange(displayStats.inProgress, liveStats.yesterday?.inProgress)} color="purple" icon={<FiTruck />} />
                            <KPITile title="Completed" value={displayStats.completed} change={getChange(displayStats.completed, liveStats.yesterday?.completed)} color="green" icon={<FiCheckCircle />} />
                            <KPITile title="Failed" value={displayStats.failed} change={getChange(displayStats.failed, liveStats.yesterday?.failed)} color="red" icon={<FiXCircle />} />
                            <KPITile title="Active Drivers" value={liveStats.activeDrivers || 0} change={`Total: ${liveStats.totalDrivers || 0}`} color="blue" icon={<FiUsers />} />
                        </>
                    )}
                </div>

                <div className="bottom-grid-dispatcher">

                    {/* ── Alerts ── */}
                    <div className="alerts-box">
                        <div className="alerts-header"><h4>Alerts & Exceptions</h4></div>
                        {ordersLoading ? (
                            <SectionLoader height={160} label="Checking alerts…" />
                        ) : (
                            alertsData.map((item, i) => (
                                <React.Fragment key={i}>
                                    <div className="alert-item">
                                        <div className="alert-left">
                                            <span className={`alert-icon ${item.color}`}>{item.icon}</span>
                                            <p>{item.label}</p>
                                        </div>
                                        <strong>{item.count}</strong>
                                    </div>
                                    <hr />
                                </React.Fragment>
                            ))
                        )}
                    </div>

                    {/* ── Urgent Queue ── */}
                    <div className="urgent-orders-box">
                        <div className="urgent-orders-box-header">
                            <h4>
                                Urgent Queue
                                {!ordersLoading && <span>{totalDelayed} orders</span>}
                                {ordersLoading && (
                                    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, color: "#94a3b8", fontWeight: 400 }}>
                                        <Spinner size={12} /> Fetching…
                                    </span>
                                )}
                            </h4>
                            <button className="show-all-btn" onClick={() => navigate("/CurrentOrder")}>Show All</button>
                        </div>

                        {ordersLoading ? (
                            <div style={{ padding: "12px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
                                {Array.from({ length: 4 }).map((_, i) => (
                                    <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                            <Skeleton width={10} height={10} radius={50} />
                                            <Skeleton width={100} height={14} />
                                        </div>
                                        <Skeleton width={60} height={28} radius={6} />
                                    </div>
                                ))}
                            </div>
                        ) : totalDelayed === 0 ? (
                            <div style={{ textAlign: "center", padding: "40px", color: "#888" }}>🚚 No delayed orders</div>
                        ) : (
                            <div className="urgent-orders-list">
                                {[
                                    ...delayed24hOrders.map(o => ({ ...o, urgencyColor: "red" })),
                                    ...delayed12hOrders.map(o => ({ ...o, urgencyColor: "orange" })),
                                    ...delayed6hOrders.map(o => ({ ...o, urgencyColor: "yellow" })),
                                ]
                                    .slice(0, 5)
                                    .map((order, index) => (
                                        <React.Fragment key={order._id || index}>
                                            <div className="urgent-order-card">
                                                <div className="urgent-left">
                                                    <div className={`urgent-dot ${order.urgencyColor}`} />
                                                    <div><strong>{order.Order_No || "No Order Id"}</strong></div>
                                                </div>
                                                <button
                                                    className={`urgent-assign-btn urgent-assign-btn--${order.urgencyColor}`}
                                                    onClick={() => handleAssignClick(order._id)}
                                                >
                                                    Assign
                                                </button>
                                            </div>
                                            <hr className="urgent-divider" />
                                        </React.Fragment>
                                    ))}
                            </div>
                        )}
                    </div>

                    {/* ── Drivers List ── */}
                    <div className="active-drivers-box">
                        <div className="drivers-header">
                            <h4>
                                Drivers List
                                {activeOrdersLoading && (
                                    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, marginLeft: 8, fontSize: 12, color: "#94a3b8", fontWeight: 400 }}>
                                        <Spinner size={12} color="#94a3b8" /> Syncing…
                                    </span>
                                )}
                            </h4>
                            {activeDriversList.length > 5 && (
                                <span style={{ cursor: "pointer", color: "#3b82f6" }} onClick={() => setShowAllDrivers(p => !p)}>
                                    {showAllDrivers ? "Show Less" : "View All"}
                                </span>
                            )}
                        </div>

                        {driversLoading ? (
                            <div style={{ padding: "10px 16px", display: "flex", flexDirection: "column", gap: 12 }}>
                                {Array.from({ length: 5 }).map((_, i) => (
                                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                        <Skeleton width={10} height={10} radius={50} />
                                        <Skeleton width={130} height={14} />
                                        <Skeleton width={30} height={22} radius={6} style={{ marginLeft: "auto" }} />
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <table>
                                <tbody>
                                    {sortedDrivers
                                        .slice(0, showAllDrivers ? sortedDrivers.length : 5)
                                        .map((d, i) => {
                                            const activeCount = activeOrdersLoading ? "…" : (driverActiveOrdersMap.get(d.email) ?? "—");
                                            return (
                                                <tr key={i} className="dName--clickable" onClick={() => handleDriverClick(d)} title="Click to view active orders">
                                                    <td>
                                                        {d.status === true
                                                            ? <span className="active-drivers-dot green" />
                                                            : <span className="active-drivers-dot red" />}
                                                    </td>
                                                    <td className="dName">{d.name}</td>
                                                    <td className="driver-active-count-span">
                                                        <span className="driver-active-count">{activeCount}</span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Assign Modal ── */}
            {showAssignModal && (
                <AssignModal
                    showModal={showAssignModal}
                    onClose={handleCloseAssignModal}
                    oid={assignOrderId}
                    actionType={assignActionType}
                    onStatusUpdated={handleAssignmentStatusUpdated}
                    selectedOrders={selectedOrders}
                    onAssignmentComplete={handleAssignmentComplete}
                    reason={assignReason}
                />
            )}

            {/* ── Driver Orders Modal ── */}
            {showDriverOrdersModal && selectedDriverForModal && (
                <div className="driver-orders-overlay" onClick={() => setShowDriverOrdersModal(false)}>
                    <div className="driver-orders-modal" onClick={e => e.stopPropagation()}>
                        <div className="driver-orders-modal__header">
                            <div className="driver-orders-modal__title-group">
                                <span className="active-drivers-dot green" style={{ marginRight: 8 }} />
                                <h3 className="driver-orders-modal__title">{selectedDriverForModal.name}</h3>
                            </div>
                            <button className="driver-orders-modal__close" onClick={() => setShowDriverOrdersModal(false)}>✕</button>
                        </div>

                        <div className="driver-orders-modal__subheader">
                            <span className="driver-orders-modal__badge">
                                {driverModalLoading ? "Loading orders…" : `${driverModalOrders.length} active order${driverModalOrders.length !== 1 ? "s" : ""}`}
                            </span>
                        </div>

                        <div className="driver-orders-modal__body">
                            {driverModalLoading ? (
                                // Skeleton cards while loading
                                <div style={{ display: "flex", flexDirection: "column", gap: 12, padding: "4px 0" }}>
                                    {Array.from({ length: 3 }).map((_, i) => (
                                        <div key={i} style={{ background: "#f8fafc", borderRadius: 10, padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>
                                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                                                <Skeleton width={90} height={14} />
                                                <Skeleton width={70} height={22} radius={20} />
                                            </div>
                                            <div style={{ display: "flex", gap: 8 }}>
                                                <Skeleton width={60} height={14} />
                                                <Skeleton width={80} height={14} />
                                            </div>
                                            <Skeleton width="80%" height={12} />
                                            <Skeleton width="60%" height={12} />
                                        </div>
                                    ))}
                                </div>
                            ) : driverModalOrders.length === 0 ? (
                                <div className="driver-orders-modal__empty">🎉 No active orders right now</div>
                            ) : (
                                driverModalOrders.map((order, idx) => {
                                    const statusMap = { "1": "Assigned", "4": "Picked Up", "5": "On the Way" };
                                    const statusClass = { "1": "status--assigned", "4": "status--picked", "5": "status--onway" };
                                    const delayMs = order.Del_Date && order.Del_Time ? new Date() - new Date(`${order.Del_Date}T${order.Del_Time}`) : null;
                                    const isDelayed = delayMs !== null && delayMs > 0;
                                    const delayHours = delayMs !== null ? Math.abs(Math.floor(delayMs / 3600000)) : null;

                                    return (
                                        <div key={order._id || idx} className="driver-order-card">
                                            <div className="driver-order-card__top">
                                                <strong className="driver-order-card__id">{order.Order_No}</strong>
                                                <span className={`driver-order-card__status ${statusClass[order.o_status] || ""}`}>
                                                    {statusMap[order.o_status] || "In Progress"}
                                                </span>
                                            </div>
                                            <div className="driver-order-card__meta">
                                                <span className="driver-order-card__amount">₹{order.amount}</span>
                                                {delayHours !== null && (
                                                    <span className={`driver-order-card__delay ${isDelayed ? "delay--late" : "delay--ok"}`}>
                                                        {isDelayed ? `⚠ Delayed ${delayHours}h` : `✓ Due in ${delayHours}h`}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="driver-order-card__addresses">
                                                <div className="driver-order-card__addr-row">
                                                    <span className="addr-dot addr-dot--pickup" />
                                                    <span>{order.Rec_Name || "—"}</span>
                                                </div>
                                                <div className="driver-order-card__addr-row">
                                                    <span className="addr-dot addr-dot--drop" />
                                                    <span className="addr-text">{order.Rec_Add || "—"}</span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default DispatcherDashboard;